import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

const MAX_ITEMS = 30;
const MAX_LEN = 150;

function clampRule(s) {
  return (s ?? '').toString().replace(/\s+/g, ' ').trim().slice(0, MAX_LEN);
}

function normalizeRules(arr) {
  const list = Array.isArray(arr) ? arr : [];
  return list
    .map(clampRule)
    .filter((x) => x.length > 0)
    .slice(0, MAX_ITEMS);
}

export function BusinessRulesModal({
  open,
  onClose,
  value, // string[]
  onSave, // (rules: string[]) => void
  saving = false,
  theme,
  pillButtonSx,
}) {
  const isDark = theme?.palette?.mode === 'dark';

  const [rules, setRules] = React.useState(() => normalizeRules(value));
  const [err, setErr] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setRules(normalizeRules(value));
      setErr('');
    }
  }, [open, value]);

  const count = rules.length;
  const canAdd = count < MAX_ITEMS;

  const addRule = () => {
    if (!canAdd) return;
    setRules((prev) => [...prev, '']);
    setErr('');
  };

  const removeRule = (idx) => {
    setRules((prev) => prev.filter((_, i) => i !== idx));
    setErr('');
  };

  const updateRule = (idx, next) => {
    const v = (next ?? '').toString().slice(0, MAX_LEN);
    setRules((prev) => prev.map((x, i) => (i === idx ? v : x)));
    setErr('');
  };

  const onSaveClick = () => {
    const normalized = normalizeRules(rules);

    if (normalized.length > MAX_ITEMS) {
      setErr(`En fazla ${MAX_ITEMS} madde ekleyebilirsin.`);
      return;
    }
    const tooLong = normalized.find((x) => x.length > MAX_LEN);
    if (tooLong) {
      setErr(`Her madde en fazla ${MAX_LEN} karakter olmalı.`);
      return;
    }

    onSave?.(normalized);
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: '18px',
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.text.primary, isDark ? 0.14 : 0.08)}`,
          boxShadow: isDark ? '0 24px 60px rgba(0,0,0,0.55)' : '0 24px 60px rgba(16,24,40,0.18)',
        },
      }}
    >
      <DialogTitle
        sx={{
          py: 1.75,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          bgcolor: isDark ? alpha('#0b1220', 0.55) : alpha('#ffffff', 0.85),
          backdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${alpha(theme.palette.text.primary, isDark ? 0.14 : 0.08)}`,
        }}
      >
        <MDBox display="flex" alignItems="center" gap={1.25}>
          <MDBox
            sx={{
              width: 34,
              height: 34,
              borderRadius: '12px',
              display: 'grid',
              placeItems: 'center',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
              bgcolor: alpha(theme.palette.primary.main, isDark ? 0.18 : 0.08),
              boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.08)}`,
            }}
          >
            <Icon
              sx={{
                fontSize: 18,
                color: theme.palette.primary.main,
              }}
            >
              rule
            </Icon>
          </MDBox>

          <MDBox>
            <MDTypography sx={{ fontWeight: 900, lineHeight: 1.1 }}>İşletme Kuralları</MDTypography>
          </MDBox>
        </MDBox>

        <Button
          onClick={addRule}
          disabled={!canAdd || saving}
          startIcon={
            <Icon
              sx={{
                fontSize: 18,
              }}
            >
              add
            </Icon>
          }
          sx={{
            ...pillButtonSx(theme, { disabled: !canAdd || saving }),

            color: theme.palette.success.main,
            border: `1px solid ${alpha(theme.palette.success.main, 0.4)}`,
            bgcolor: alpha(theme.palette.success.main, 0.08),

            '&:hover': {
              bgcolor: alpha(theme.palette.success.main, 0.16),
              border: `1px solid ${theme.palette.success.main}`,
              boxShadow: `0 0 0 4px ${alpha(theme.palette.success.main, 0.15)}`,
            },

            '& .MuiSvgIcon-root': {
              color: theme.palette.success.main,
            },
          }}
        >
          Madde Ekle
        </Button>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        <MDTypography sx={{ fontSize: 12.5, opacity: 0.72, mb: 1 }}>
          En fazla <b>{MAX_ITEMS}</b> madde, her madde en fazla <b>{MAX_LEN}</b> karakter.
        </MDTypography>

        {err ? (
          <Alert severity="error" sx={{ mb: 1, borderRadius: '14px' }}>
            {err}
          </Alert>
        ) : null}

        <MDBox
          sx={{
            p: 1.25,
            borderRadius: '16px',
            border: `1px solid ${alpha(theme.palette.text.primary, isDark ? 0.14 : 0.08)}`,
            bgcolor: isDark ? alpha('#0b1220', 0.22) : alpha('#0b1220', 0.02),
          }}
        >
          <Stack
            spacing={1}
            sx={{
              maxHeight: 420,
              overflow: 'auto',
              pr: 0.5,
              '&::-webkit-scrollbar': { width: 10 },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: 10,
                background: alpha(theme.palette.text.primary, isDark ? 0.14 : 0.12),
                border: `3px solid transparent`,
                backgroundClip: 'content-box',
              },
            }}
          >
            {rules.length === 0 ? (
              <MDBox
                sx={{
                  p: 2,
                  borderRadius: '14px',
                  border: `1px dashed ${alpha(theme.palette.text.primary, isDark ? 0.18 : 0.16)}`,
                  bgcolor: isDark ? alpha('#0b1220', 0.18) : alpha('#0b1220', 0.02),
                }}
              >
                <MDTypography sx={{ fontSize: 12.5, opacity: 0.7 }}>
                  Henüz kural eklenmedi. Üstten “Madde Ekle” ile başlayabilirsin.
                </MDTypography>
              </MDBox>
            ) : null}

            {rules.map((r, idx) => {
              const len = (r ?? '').length;
              const over = len > MAX_LEN;

              return (
                <MDBox
                  key={`rule-${idx}`}
                  sx={{
                    p: 1,
                    borderRadius: '14px',
                    border: `1px solid ${alpha(theme.palette.text.primary, isDark ? 0.14 : 0.08)}`,
                    bgcolor: isDark ? alpha('#0b1220', 0.25) : alpha('#ffffff', 0.7),
                    backdropFilter: 'blur(6px)',
                  }}
                >
                  <MDBox
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      mb: 0.75,
                    }}
                  >
                    <Chip
                      size="small"
                      label={`Madde ${idx + 1}`}
                      sx={{
                        fontWeight: 800,
                        borderRadius: '10px',
                        bgcolor: isDark ? alpha('#ffffff', 0.08) : alpha('#0b1220', 0.04),
                        border: `1px solid ${alpha(
                          theme.palette.text.primary,
                          isDark ? 0.14 : 0.08
                        )}`,
                      }}
                    />

                    <IconButton
                      onClick={() => removeRule(idx)}
                      size="small"
                      disabled={saving}
                      sx={{
                        borderRadius: '12px',
                        border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                        bgcolor: alpha(theme.palette.error.main, 0.08),
                        '&:hover': {
                          bgcolor: alpha(theme.palette.error.main, 0.15),
                          boxShadow: `0 0 0 4px ${alpha(theme.palette.error.main, 0.12)}`,
                        },
                      }}
                    >
                      <Icon
                        sx={{
                          fontSize: 18,
                          color: theme.palette.error.main,
                        }}
                      >
                        delete
                      </Icon>
                    </IconButton>
                  </MDBox>

                  {/* ✅ daha büyük alan */}
                  <TextField
                    value={r}
                    onChange={(e) => updateRule(idx, e.target.value)}
                    placeholder="Kuralı yaz…"
                    fullWidth
                    multiline
                    minRows={3}
                    maxRows={8}
                    size="small"
                    error={over}
                    helperText={`${len}/${MAX_LEN}`}
                    inputProps={{ maxLength: MAX_LEN }}
                    sx={{
                      '& .MuiInputBase-root': {
                        borderRadius: '14px',
                        bgcolor: isDark ? alpha('#0b1220', 0.25) : alpha('#0b1220', 0.015),
                      },
                      '& .MuiFormHelperText-root': { opacity: 0.7 },
                    }}
                  />
                </MDBox>
              );
            })}
          </Stack>
        </MDBox>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          display: 'flex',
          gap: 1,
          borderTop: `1px solid ${alpha(theme.palette.text.primary, isDark ? 0.14 : 0.08)}`,
          bgcolor: isDark ? alpha('#0b1220', 0.35) : alpha('#ffffff', 0.85),
          backdropFilter: 'blur(10px)',
        }}
      >
        <Button onClick={onClose} disabled={saving} sx={pillButtonSx(theme, { disabled: saving })}>
          Vazgeç
        </Button>

        <Button
          onClick={onSaveClick}
          disabled={saving}
          startIcon={
            saving ? (
              <CircularProgress size={16} sx={{ color: '#fff' }} />
            ) : (
              <Icon sx={{ fontSize: 18 }}>save</Icon>
            )
          }
          sx={{
            ...pillButtonSx(theme, { disabled: saving }),

            bgcolor: theme.palette.success.main,
            color: '#fff',
            border: `1px solid ${theme.palette.success.main}`,

            '&:hover': {
              bgcolor: theme.palette.success.dark,
              boxShadow: `0 0 0 4px ${alpha(theme.palette.success.main, 0.25)}`,
            },

            '&.Mui-disabled': {
              bgcolor: alpha(theme.palette.success.main, 0.4),
              color: '#fff',
            },
          }}
        >
          Kaydet
        </Button>
      </DialogActions>
    </Dialog>
  );
}
